/**
 * Shared location → Existing/New grouping (#999 Epic 3.1).
 *
 * Consumed by step lists (3.2), Location view (3.3), Summary, and PDF so grouping
 * stays aligned (FR17–FR18, NFR10, NFR14).
 */

import type { LocationTag } from '../types/locationTag';

export type RepeatableUnitStatusBucket = 'existing' | 'new';

export interface LocationStatusGroupedUnits<T> {
  existing: T[];
  new: T[];
}

export interface LocationUnitGroup<T> {
  locationTagId: number | null;
  locationTagName: string | null;
  isUntagged: boolean;
  byStatus: LocationStatusGroupedUnits<T>;
}

export type LocationTagGroupingInput = Pick<LocationTag, 'id' | 'name'>;

/** Server ids ASC; draft client ids (negative) sort -1 before -2. */
export function compareLocationTagCreationOrder(
  aId: number | undefined,
  bId: number | undefined,
): number {
  const a = aId ?? 0;
  const b = bId ?? 0;
  if (a < 0 && b < 0) {
    return b - a;
  }
  return a - b;
}

export function sortLocationTagsByCreationOrder(
  tags: LocationTagGroupingInput[],
): LocationTagGroupingInput[] {
  return [...tags].sort((a, b) => compareLocationTagCreationOrder(a.id, b.id));
}

export interface GroupUnitsByLocationThenStatusInput<T> {
  tags: LocationTagGroupingInput[];
  units: T[];
  getLocationTagId: (unit: T) => number | null | undefined;
  getStatus: (unit: T) => string | undefined;
  getDisplayOrder?: (unit: T) => number;
  /** When true, emit a group for every tag in creation order (empty buckets allowed). */
  includeEmptyTagGroups?: boolean;
}

export function normalizeRepeatableUnitStatus(
  status: string | undefined,
): RepeatableUnitStatusBucket {
  return status === 'existing' ? 'existing' : 'new';
}

function partitionUnitsByStatus<T>(
  units: T[],
  getStatus: (unit: T) => string | undefined,
  getDisplayOrder: (unit: T) => number,
): LocationStatusGroupedUnits<T> {
  const existing: T[] = [];
  const newUnits: T[] = [];

  for (const unit of units) {
    if (normalizeRepeatableUnitStatus(getStatus(unit)) === 'existing') {
      existing.push(unit);
    } else {
      newUnits.push(unit);
    }
  }

  const sortByDisplayOrder = (a: T, b: T) =>
    getDisplayOrder(a) - getDisplayOrder(b);

  existing.sort(sortByDisplayOrder);
  newUnits.sort(sortByDisplayOrder);

  return { existing, new: newUnits };
}

function isKnownLocationTagId(
  locationTagId: number | null | undefined,
  knownTagIds: Set<number>,
): locationTagId is number {
  return (
    locationTagId !== null &&
    locationTagId !== undefined &&
    knownTagIds.has(locationTagId)
  );
}

/**
 * Groups units by location tag (creation order), then Untagged, with Existing/New
 * buckets inside each location group.
 */
export function groupUnitsByLocationThenStatus<T>(
  input: GroupUnitsByLocationThenStatusInput<T>,
): LocationUnitGroup<T>[] {
  const {
    tags,
    units,
    getLocationTagId,
    getStatus,
    getDisplayOrder = () => 0,
    includeEmptyTagGroups = false,
  } = input;

  const sortedTags = sortLocationTagsByCreationOrder(tags);
  const knownTagIds = new Set(
    sortedTags.flatMap(tag => (tag.id !== undefined ? [tag.id] : [])),
  );
  const groups: LocationUnitGroup<T>[] = [];

  for (const tag of sortedTags) {
    if (tag.id === undefined) {
      continue;
    }

    const tagUnits = units.filter(unit => getLocationTagId(unit) === tag.id);

    if (!includeEmptyTagGroups && tagUnits.length === 0) {
      continue;
    }

    groups.push({
      locationTagId: tag.id,
      locationTagName: tag.name,
      isUntagged: false,
      byStatus: partitionUnitsByStatus(tagUnits, getStatus, getDisplayOrder),
    });
  }

  const untaggedUnits = units.filter(unit => {
    const locationTagId = getLocationTagId(unit);
    return !isKnownLocationTagId(locationTagId, knownTagIds);
  });

  groups.push({
    locationTagId: null,
    locationTagName: null,
    isUntagged: true,
    byStatus: partitionUnitsByStatus(untaggedUnits, getStatus, getDisplayOrder),
  });

  return groups;
}

/** Flatten grouped units in render order: location groups → existing → new. */
export function flattenLocationUnitGroups<T>(
  groups: LocationUnitGroup<T>[],
): T[] {
  const flattened: T[] = [];
  for (const group of groups) {
    flattened.push(...group.byStatus.existing, ...group.byStatus.new);
  }
  return flattened;
}
